import { formatTranslation } from '@/common/utils/formatTranslation';
import { getCharacterAttributeFallback } from './profileCharacterFallbacks';
import { formatProfileKinks } from './resolveProfileKinks';

const inferBodyType = (profile) => {
	const tags = [
		...(profile?.person_config?.tags || []),
		...(profile?.tag?.map((tag) => tag.label) || []),
	].map((tag) => String(tag).toLowerCase());

	if (
		tags.some((tag) =>
			['fitness', 'sport', 'trainer', 'athletic', 'gym'].includes(tag),
		)
	) {
		return 'Athletic';
	}

	if (tags.some((tag) => ['luxury', 'elegant'].includes(tag))) {
		return 'Elegant';
	}

	return profile?.body_type || profile?.style || 'Fit';
};

const getPersonalityValue = (profile, label) => {
	const entry = profile?.personality?.find(
		(item) => item?.label?.toLowerCase() === label.toLowerCase(),
	);

	return entry?.value || null;
};

export const resolveProfileOrigin = (profile, t) => {
	const fallback = getCharacterAttributeFallback(profile);

	return (
		profile?.origin
		|| profile?.country
		|| profile?.nationality
		|| profile?.person_config?.origin
		|| profile?.person_config?.country
		|| profile?.person_config?.city
		|| fallback?.origin
		|| t.defaultOrigin
	);
};

export const resolveProfileCity = (profile, t) => (
	profile?.city
	|| profile?.person_config?.city
	|| profile?.country
	|| profile?.person_config?.country
	|| t.defaultOrigin
);

export const getProfileMeta = (profile, t) => {
	const origin = resolveProfileOrigin(profile, t);
	const age = profile?.age ? String(profile.age) : t.unknown;

	return formatTranslation(t.meta, { age, origin });
};

const normalizeGalleryFileId = (fileRef) => {
	const id = fileRef?._id ?? fileRef;

	if (!id) {
		return null;
	}

	return String(id);
};

export const getProfileGallerySlides = (profile) => {
	const slides = [];
	const seenIds = new Set();

	const videoId = normalizeGalleryFileId(
		profile?.profile_video?._id
		?? profile?.profile_video
		?? profile?.video?._id
		?? profile?.video,
	);

	if (videoId) {
		slides.push({ type: 'video', fileId: videoId });
		seenIds.add(videoId);
	}

	const profileImageId = normalizeGalleryFileId(profile?.profile_image);

	if (profileImageId) {
		slides.push({ type: 'image', fileId: profileImageId });
		seenIds.add(profileImageId);
	}

	(profile?.stories_images || []).forEach((storyRef) => {
		const storyId = normalizeGalleryFileId(storyRef);

		if (storyId && !seenIds.has(storyId)) {
			seenIds.add(storyId);
			slides.push({ type: 'image', fileId: storyId });
		}
	});

	return slides;
};

/** @deprecated Use getProfileGallerySlides */
export const getProfileGalleryFileIds = (profile) => (
	getProfileGallerySlides(profile)
		.filter((slide) => slide.type === 'image')
		.map((slide) => slide.fileId)
);

export const getProfileAttributes = (profile, t, lang = 'en') => {
	const fallback = getCharacterAttributeFallback(profile);

	const occupation =
		profile?.occupation
		|| profile?.person_config?.occupation
		|| getPersonalityValue(profile, 'Occupation')
		|| fallback?.occupation
		|| t.defaultOccupation;

	const relationship =
		profile?.marital_status
		|| profile?.relationship
		|| profile?.person_config?.relationship_status
		|| getPersonalityValue(profile, 'Relationship')
		|| fallback?.relationship
		|| t.defaultRelationship;

	const kinksValue = formatProfileKinks(profile, lang, t, fallback);

	const country = resolveProfileOrigin(profile, t);

	const ageValue = profile?.age ? String(profile.age) : t.unknown;

	const bodyType =
		profile?.body_type
		|| profile?.style
		|| profile?.person_config?.body_type
		|| fallback?.body_type
		|| inferBodyType(profile);

	const zodiac =
		profile?.zodiac
		|| profile?.zodiac_sign
		|| profile?.person_config?.zodiac_sign
		|| getPersonalityValue(profile, 'Zodiac')
		|| fallback?.zodiac
		|| t.defaultZodiac;

	return [
		{
			title: t.personalityAttributes,
			items: [
				{ icon: 'occupation', label: t.occupation, value: occupation },
				{ icon: 'relationship', label: t.relationship, value: relationship },
				{ icon: 'kinks', label: t.kinks, value: kinksValue },
			],
		},
		{
			title: t.physicalAttributes,
			items: [
				{ icon: 'country', label: t.country, value: country },
				{ icon: 'age', label: t.age, value: ageValue },
				{ icon: 'body', label: t.bodyType, value: bodyType },
				{ icon: 'zodiac', label: t.zodiacSign, value: zodiac },
			],
		},
	];
};
